import React from "react";
import {
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import * as Switch from "@radix-ui/react-switch";
import FlashTeamLogo from "@/assets/flashteam-logo.svg";
import FlashTeamLogoBlack from "@/assets/flashteam-logo-black.png";
import HotspringLogo from "@/assets/logo.svg";
import HotspringLogoBlack from "@/assets/logo-black.png";
import SwitchTeamIcon from "@/assets/switch-team.svg";
import ThemeIcon from "@/assets/bulb.svg";
import LogoutIcon from "@/assets/log-out.svg";
import { Settings } from "lucide-react";
import { useUserContext } from "@/contexts/UserContextProvider.tsx";
import { Button } from "@/components/ui/button.tsx";
import { LOGIN_URL } from "@/helpers/url.ts";
import { Theme, useTheme } from "@/contexts/ThemeContext.tsx";
import { CompanyRole } from "@/__generated__/graphql";
import { SUBCONTRACTING_FOR_USER } from "@/env.ts";
import LongContentWrapper from "../common/LongContentWrapper";

interface ProfileDropdownProps {
  setShowSettings: (showSettings: boolean) => void;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  setShowSettings,
}) => {
  const { currentUser, role, currentProject } = useUserContext();
  const { theme, toggleTheme, ref } = useTheme();

  const logout = () => {
    localStorage.clear();
    window.location.href = LOGIN_URL;
  };

  const getRedirectUrl = () => {
    const projectId =
      role === CompanyRole.Vendor
        ? currentProject?.subcontractedProject?.id
        : currentProject?.sourceProject?.id;

    let path = location.pathname;

    if (projectId) {
      const encodedProjectId = encodeURIComponent(projectId);
      if (path.startsWith("/projects/")) {
        path = path.replace(
          /\/projects\/[^/]+/,
          `/projects/${encodedProjectId}`,
        );
      } else {
        path = `/projects/${encodedProjectId}${path}`;
      }
    } else {
      path = path.replace(/^\/projects\/[^/]+/, "");
    }

    return path;
  };

  return (
    <div className="py-2 text-sm text-secondary-foreground w-[220px]">
      <div className="px-3 flex flex-row items-center gap-2 w-full">
        {currentUser?.profilePictureUrl ? (
          <img
            src={currentUser?.profilePictureUrl}
            alt="Profile"
            className="!min-w-10 !min-h-10 !max-w-10 !max-h-10 border border-secondary-foreground rounded-full"
          />
        ) : (
          <div className="!min-w-10 !min-h-10 !max-w-10 !max-h-10 border border-secondary-foreground rounded-full bg-gray-200 dark:bg-transparent flex items-center">
            <span className="flex items-center justify-center w-full h-full text-sm font-medium text-black dark:text-white">
              {currentUser?.firstName?.charAt(0).toUpperCase()}
              {currentUser?.lastName?.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div className="max-w-[calc(100%-40px)]">
          <LongContentWrapper className="text-base text-black dark:text-white !max-w-full">
            {currentUser?.firstName} {currentUser?.lastName}
          </LongContentWrapper>
          <LongContentWrapper>{currentUser?.username}</LongContentWrapper>
        </div>
      </div>

      <DropdownMenuSeparator />

      <div className="flex flex-col">
        {currentUser?.hasSubcontracting && (
          <Button
            variant="ghost"
            onClick={async () => {
              if (role === CompanyRole.Vendor) {
                localStorage.setItem(SUBCONTRACTING_FOR_USER, "True");
              } else {
                localStorage.removeItem(SUBCONTRACTING_FOR_USER);
              }
              window.location.href = getRedirectUrl();
            }}
            className="justify-start group hover:bg-accent hover:text-black dark:hover:text-white text-sm flex justify-between items-center"
          >
            <div className="flex items-center gap-[10px]">
              <img
                src={SwitchTeamIcon}
                alt="Switch team"
                className="invert dark:invert-0"
              />
              Switch to
            </div>
            <img
              src={
                role == CompanyRole.Vendor
                  ? theme == Theme.Dark
                    ? FlashTeamLogo
                    : FlashTeamLogoBlack
                  : theme == Theme.Dark
                    ? HotspringLogo
                    : HotspringLogoBlack
              }
              className="w-[60px]"
              alt="Logo"
            />
          </Button>
        )}

        <DropdownMenuItem
          className="px-4 justify-start group hover:bg-accent focus:text-black hover:text-black dark:hover:text-white cursor-pointer text-sm text-bold"
          onClick={() => {
            setShowSettings(true);
          }}
        >
          <Settings className="text-primary-foreground" />
          Settings
        </DropdownMenuItem>

        <div className="flex items-center justify-between px-4 py-2 text-sm">
          <div className="flex items-center gap-2">
            <img
              src={ThemeIcon}
              alt="Change theme"
              className="w-4 h-4 invert dark:invert-0"
            />
            Dark Mode
          </div>
          <Switch.Root
            ref={ref}
            checked={theme === "dark"}
            onCheckedChange={toggleTheme}
            className="relative inline-flex items-center w-8 h-4 bg-[#F0F0F0] dark:bg-black rounded-[12px] p-[2px] cursor-pointer"
          >
            <Switch.Thumb className="block w-[12px] h-[12px] rounded-full bg-[#FF744D] shadow-[0px_1px_2px_0px_#1018280F,_0px_1px_3px_0px_#1018281A] transition-transform duration-200 translate-x-0 data-[state=checked]:translate-x-4" />
          </Switch.Root>
        </div>
      </div>

      <DropdownMenuSeparator />

      <Button
        variant="ghost"
        onClick={logout}
        className="justify-start w-full px-5 gap-2 group hover:bg-accent hover:text-black dark:hover:text-white"
      >
        <img src={LogoutIcon} alt="Log out" className="invert dark:invert-0" />
        Log out
      </Button>
    </div>
  );
};

export default ProfileDropdown;
